import React from 'react'
import { ChevronDown } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { ProductDto } from '@wtf/shared'
import { AddToCollectionPanel } from '@/components/collections/AddToCollectionPanel'
import { ProductOverlay } from '@/components/products/ProductOverlay'
import { SaveButton } from '@/components/shared/atoms/SaveButton'
import { useCurrentCollectionToggle } from '@/hooks/useCurrentCollectionToggle'
import { useClickOutside } from '@/hooks/useClickOutside'
import { DropdownPortal } from '@/components/shared/DropdownPortal'

type ElementType =
    | HTMLElement
    | HTMLButtonElement
    | HTMLDivElement
    | HTMLInputElement
    | HTMLFormElement
    | Element

interface DesktopOverlayProps {
    product: ProductDto
    productId: string
    onMouseLeave: () => void
    menuOpen: boolean
    setMenuOpen: (open: boolean) => void
    hideOverlayBackground?: boolean
}

export const DesktopOverlay: React.FC<DesktopOverlayProps> = ({
    product,
    productId,
    onMouseLeave,
    menuOpen,
    setMenuOpen,
    hideOverlayBackground = false,
}) => {
    const buttonRef = React.useRef<HTMLButtonElement>(null)
    const panelRef = React.useRef<HTMLDivElement>(null)
    const [isCreateFlowOpen, setIsCreateFlowOpen] = React.useState(false)
    const containerRef = React.useRef<HTMLDivElement>(null)

    const {
        handleToggle,
        isProductInCurrentCollection,
        currentCollection,
        isInitialLoading,
    } = useCurrentCollectionToggle(product)

    // Handle click outside for dropdown panel - disable when create flow is open
    useClickOutside(
        [buttonRef, panelRef] as React.RefObject<ElementType | null>[],
        () => setMenuOpen(false),
        { enabled: menuOpen && !isCreateFlowOpen }
    )

    const toggleMenu = () => {
        setMenuOpen(!menuOpen)
    }

    // Display current collection name with loading state
    // Shows "Select Collection" when loading or when no collection exists
    const displayText = isInitialLoading
        ? 'Select Collection'
        : currentCollection?.name || 'Select Collection'

    return (
        <div
            className={`absolute inset-0 rounded-2xl md:block ${
                menuOpen ? 'opacity-100' : 'opacity-0 hover:opacity-100'
            }`}
            onMouseLeave={onMouseLeave}
            ref={containerRef}
        >
            <Link
                to="/products/$id"
                params={{ id: productId }}
                onClick={(e) => {
                    if (menuOpen) {
                        e.preventDefault()
                        e.stopPropagation()
                    }
                }}
                className={`flex h-full flex-col rounded-2xl ${hideOverlayBackground ? '' : 'bg-black/50'}`}
            >
                <div className="mx-2 mt-2 flex items-center justify-between gap-2">
                    <section className="min-w-0 flex-1">
                        <button
                            ref={buttonRef}
                            type="button"
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                toggleMenu()
                            }}
                            className={`flex max-w-full cursor-pointer items-center gap-1 rounded-xl px-3 py-2 ${hideOverlayBackground ? 'bg-black/50 hover:bg-black/70' : 'hover:bg-black/50'}`}
                        >
                            <span className="truncate text-sm text-white">
                                {displayText}
                            </span>
                            <ChevronDown
                                className="shrink-0 text-white"
                                width={16}
                                height={16}
                            />
                        </button>

                        <DropdownPortal
                            anchorRef={containerRef}
                            containerRef={containerRef}
                        >
                            <div className="w-full">
                                <AddToCollectionPanel
                                    productId={productId}
                                    open={menuOpen}
                                    onClose={() => setMenuOpen(false)}
                                    onCreateFlowOpenChange={setIsCreateFlowOpen}
                                    ref={panelRef}
                                />
                            </div>
                        </DropdownPortal>
                    </section>

                    <section className="shrink-0">
                        <SaveButton
                            isSaved={isProductInCurrentCollection}
                            size="sm"
                            variant="on-dark"
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                handleToggle()
                            }}
                            className="flex items-center gap-2"
                        />
                    </section>
                </div>
                <ProductOverlay product={product} variant="bottom" />
            </Link>
        </div>
    )
}
